import { useQuery, useReactiveVar } from '@apollo/client'
import { kebabCase, lowerCase } from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BusinessSegmentCategories } from '@src/components/BusinessSegments/BusinessSegmentCategories'
import {
  BusinessSegmentOutlets,
  OutletOrLinkCard,
} from '@src/components/BusinessSegments/BusinessSegmentOutlets'
import { ErrorPage } from '@src/components/Errors/ErrorPage'
import {
  BusinessSegment,
  BusinessSegmentType,
  Cuisine,
  LocationType,
  MerchantType,
} from '@src/graphql-types'
import { clearNonBasketOutletFulfilments } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/utils/clearNonBasketFulfilments'
import { useMarketplaceDeliveryZonesQuery } from '@src/hooks/sharedQueries/useMarketplaceDeliveryZones/useMarketplaceDeliveryZones'
import { useCategories } from '@src/hooks/useCategories/useCategories'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { parseOutletDates } from '@src/utils/fulfilmentTimes/parsers'
import { Capsule, CapsuleContainer } from '@src/components/Capsule'

import { CategoryFilteredOutlets } from './CategoryFilteredOutlets'
import { getOutletSearchVariables } from './getOutletSearchVariables'
import { OutletCards } from './OutletCards'
import {
  AnimatedSection,
  FulfilmentCapsuleWrapper,
  HeaderContainer,
  OutletListWithCategorySideMenuContainer,
  OutletWrapper,
} from './OutletList.styles'
import { OutletListCompactSkeleton } from './OutletListCompactSkeleton'
import { AlertModal } from '@src/components/Modal/AlertModal'

import { showAppBannerVar } from '@src/components/SiteHeader/utils/reactiveVar'
import { OutletListAndSegmentsDocument } from './queries/__generated__/OutletListAndSegments.query.graphql-interface'
import { sortOutletsByMostCategories } from '../../utils/sortOutletsByMostCategories'
import { apolloErrorParser } from '../../utils/apolloErrorParser/apolloErrorParser'
import { useBottomSheetStack } from '../../hooks/useBottomSheetStack/useBottomSheetStack'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { useLocation } from 'react-router-dom'
import { CategorySideMenu } from '@src/components/Categories/CategorySideMenu'

export const OutletList: React.FC<{ deliveryZone?: string }> = ({
  deliveryZone,
}) => {
  const {
    postcodeBottomSheetController,
    serviceComponentBottomSheetController,
  } = useBottomSheetStack()
  const { isMobile, isSmallerThanDesktop } = useBreakpoint()
  const marketplace = useMarketplace()
  const fulfilmentFilter = useFulfilmentFilter()
  const { selectedCategories } = useCategories()
  const location = useLocation()
  const hasCategoriesParam = new URLSearchParams(location.search).has(
    'categories'
  )
  const showAppBanner = useReactiveVar(showAppBannerVar)
  const categoryIds = selectedCategories.map(({ id }) => id)
  const { t: serviceNavigationTranslation } =
    useTranslation('serviceNavigation')
  const { t: navigationTranslation } = useTranslation('navigation')
  const { t: errorTranslation } = useTranslation('errors')
  const { t } = useTranslation('copy')

  const merchantTypeMap = {
    [MerchantType.RESTAURANT]: serviceNavigationTranslation('all_restaurants'),
    [MerchantType.RETAIL]: serviceNavigationTranslation('all_outlets'),
  }
  const allOutletsText =
    marketplace.allOutletsText ||
    (isSmallerThanDesktop
      ? t('all')
      : merchantTypeMap[marketplace.merchantType])

  const deliveryZoneResponse = useMarketplaceDeliveryZonesQuery({
    useQueryArgs: {
      skip:
        fulfilmentFilter.data.where.location.type !==
        LocationType.DELIVERY_ZONE,
    },
  })

  const outletListVariables = getOutletSearchVariables({
    fulfilmentFilter: fulfilmentFilter.data,
    marketplaceId: marketplace.id,
    cuisineIds: categoryIds,
  })

  const {
    data,
    loading: loading,
    error: error,
  } = useQuery(OutletListAndSegmentsDocument, {
    variables: {
      ...outletListVariables,
      marketplaceId: marketplace.id,
      businessSegmentTypes: [
        BusinessSegmentType.OUTLETS,
        BusinessSegmentType.CATEGORIES,
      ],
    },
    onCompleted: () => {
      postcodeBottomSheetController.close()
    },
  })

  const parsedError = error && apolloErrorParser(error)
  const isPostcodeError = parsedError?.customClientErrors.some(
    err => err.data.errorResolutionType === 'POSTCODE_NOT_FOUND'
  )

  useEffect(() => {
    clearNonBasketOutletFulfilments()
  }, [])

  useEffect(() => {
    if (
      deliveryZone &&
      deliveryZoneResponse.data &&
      deliveryZoneResponse.data?.deliveryZonesByMarketplaceId.length > 0
    ) {
      const selectedZoneId =
        deliveryZoneResponse.data?.deliveryZonesByMarketplaceId.find(
          zone => lowerCase(kebabCase(zone.name)) === lowerCase(deliveryZone)
        )?.id

      if (
        selectedZoneId &&
        (fulfilmentFilter.data.where.location.type !==
          LocationType.DELIVERY_ZONE ||
          fulfilmentFilter.data.where.location.zoneId !== selectedZoneId)
      ) {
        fulfilmentFilter.setLocationType({
          type: LocationType.DELIVERY_ZONE,
          zoneId: selectedZoneId,
        })
      }
    }
  }, [deliveryZone, deliveryZoneResponse, fulfilmentFilter])

  const outletsWithParsedDates = useMemo(
    () => (data?.outlets ? data?.outlets.map(parseOutletDates) : []),
    [data?.outlets]
  )

  const allFulfilmentMethods = fulfilmentFilter.availableFulfilmentMethods

  const fulfilmentsTitle =
    fulfilmentFilter.data.nonEmptyFulfilmentMethods.length ===
    allFulfilmentMethods.length
      ? allOutletsText
      : fulfilmentFilter.data.nonEmptyFulfilmentMethods
          .map(fulfilmentMethod => t(fulfilmentMethod.toLowerCase()))
          .join(' and ')

  const allSegments = data?.businessSegments || []

  const outlets = sortOutletsByMostCategories(
    outletsWithParsedDates,
    selectedCategories
  )

  return (
    <OutletWrapper showAppBanner={showAppBanner}>
      {hasCategoriesParam ? (
        <CategoryFilteredOutlets loading={loading} outlets={outlets} />
      ) : (
        <>
          {loading && (
            <AnimatedSection $isVisible>
              <OutletListCompactSkeleton outlets={false} />
            </AnimatedSection>
          )}

          {allSegments.map(segment => (
            <AnimatedSection key={segment.id} $isVisible={!loading}>
              {segment.type === BusinessSegmentType.OUTLETS && (
                <BusinessSegmentOutlets
                  businessSegment={{
                    ...segment,
                    cards: segment.cards.map(card =>
                      card.__typename === 'Outlet'
                        ? parseOutletDates(card)
                        : card
                    ) as OutletOrLinkCard[],
                  }}
                />
              )}
              {segment.type === BusinessSegmentType.CATEGORIES && (
                <BusinessSegmentCategories
                  segment={
                    segment as Pick<
                      BusinessSegment,
                      'id' | 'name' | 'showName' | 'description'
                    > & {
                      cards: Cuisine[]
                    }
                  }
                />
              )}
            </AnimatedSection>
          ))}

          {outlets.length > 0 &&
            fulfilmentFilter.savedData.sortedFulfilmentMethods.length > 1 && (
              <AnimatedSection $isVisible={!loading}>
                <HeaderContainer $isVisible={!loading}>
                  {fulfilmentsTitle}
                </HeaderContainer>
              </AnimatedSection>
            )}

          {/* Quick-switch fulfilment bar */}
          {fulfilmentFilter.savedData.sortedFulfilmentMethods.length === 1 && (
            <AnimatedSection $isVisible>
              <FulfilmentCapsuleWrapper>
                <CapsuleContainer>
                  {fulfilmentFilter.availableFulfilmentMethods.map(
                    fulfilment => (
                      <Capsule
                        key={fulfilment}
                        onClick={event => {
                          fulfilmentFilter.setActiveFulfilmentMethods([
                            fulfilment,
                          ])
                          if (isMobile) {
                            event.currentTarget.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            })
                          }
                        }}
                        active={fulfilmentFilter.data.nonEmptyFulfilmentMethods.includes(
                          fulfilment
                        )}
                      >
                        {t(fulfilment.toLowerCase())}
                      </Capsule>
                    )
                  )}
                </CapsuleContainer>
              </FulfilmentCapsuleWrapper>
            </AnimatedSection>
          )}

          {!isMobile && marketplace.featureCategoriesSideMenu && (
            <AnimatedSection $isVisible={!loading}>
              <OutletListWithCategorySideMenuContainer>
                <div style={{ flex: '0 0 200px' }}>
                  <CategorySideMenu />
                </div>
                <div style={{ flex: '1' }}>
                  <OutletCards outlets={outlets} />
                </div>
              </OutletListWithCategorySideMenuContainer>
            </AnimatedSection>
          )}

          {(isMobile || !marketplace.featureCategoriesSideMenu) &&
            outlets.length > 0 && (
              <AnimatedSection $isVisible={!loading}>
                <OutletCards outlets={outlets} />
              </AnimatedSection>
            )}

          {loading && (
            <AnimatedSection $isVisible>
              <OutletListCompactSkeleton segments={false} />
            </AnimatedSection>
          )}

          {error && (
            <AnimatedSection $isVisible>
              {isPostcodeError &&
              fulfilmentFilter.data.where.location.type ===
                LocationType.POSTCODE ? (
                <AlertModal
                  isOpen={
                    isPostcodeError &&
                    !postcodeBottomSheetController.isOpen &&
                    !serviceComponentBottomSheetController.isOpen
                  }
                  title={errorTranslation('postcode_error_title')}
                  subTitle={errorTranslation('postcode_error_subtitle')}
                  cancel={{
                    text: navigationTranslation('show_everywhere'),
                    onClick: () => {
                      fulfilmentFilter.setLocationType({
                        type: LocationType.EVERYWHERE,
                      })
                    },
                  }}
                  action={{
                    text: navigationTranslation('new_postcode'),
                    onClick: () => {
                      if (isMobile) {
                        serviceComponentBottomSheetController.open()
                      }
                      postcodeBottomSheetController.open()
                    },
                  }}
                />
              ) : (
                <ErrorPage logError={error} />
              )}
            </AnimatedSection>
          )}
        </>
      )}
    </OutletWrapper>
  )
}
